import { indexMap, parseCellId, type GameState, type MapDef, type Side } from '@brig/rules-engine';
import { palette } from '@brig/theme/tokens';
import { useEffect, useMemo, useRef } from 'react';
import type { Camera } from './camera.ts';
import { CELL } from './geometry.ts';
import { toView, toWorld, type Projection } from './projection.ts';
import { flatTerrainOf } from './terrain.ts';
import { chartMinimap, type MapStyle } from './chartStyle.ts';

const chartMinimaps = new WeakMap<MapDef, HTMLCanvasElement>();
/** The chart's minimap picture of a map, made once. */
function chartMinimapOf(map: MapDef): HTMLCanvasElement {
  let c = chartMinimaps.get(map);
  if (!c) {
    c = chartMinimap(map);
    chartMinimaps.set(map, c);
  }
  return c;
}
import * as css from './Board.css.ts';

export interface MinimapProps {
  readonly state: GameState;
  /** Each side's player colour, as #rrggbb. */
  readonly colours: Readonly<Record<Side, string>>;
  readonly camera: Camera;
  /** The board's projection: the minimap stays flat and maps through it. */
  readonly projection: Projection;
  readonly mapStyle: MapStyle;
  /** Longest side in CSS px. */
  readonly size?: number;
}

/**
 * The whole map at a few px per cell, with the ships, the forts and the camera's view. Click
 * or drag on it to move the camera. Plain 2D canvas: the terrain is drawn once per map, the
 * rest on every camera or state change.
 */
export function Minimap({ state, colours, camera, projection, mapStyle, size = 200 }: MinimapProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { map } = state;
  const px = Math.max(1, Math.floor(size / Math.max(map.width, map.height)));
  const width = map.width * px;
  const height = map.height * px;
  const terrain = useMemo(() => drawTerrain(map, px, colours, mapStyle), [map, px, colours, mapStyle]);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (terrain) ctx.drawImage(terrain, 0, 0, width, height);
      for (const f of state.forts) {
        const p = parseCellId(f.cell);
        if (!p) continue;
        ctx.fillStyle = f.destroyed ? palette.inkSoft : palette.paper;
        ctx.strokeStyle = palette.ink;
        ctx.lineWidth = 1;
        const s = Math.max(3, px * 1.2);
        ctx.fillRect(p.x * px + px / 2 - s / 2, p.y * px + px / 2 - s / 2, s, s);
        ctx.strokeRect(p.x * px + px / 2 - s / 2, p.y * px + px / 2 - s / 2, s, s);
      }
      for (const s of state.ships) {
        const p = parseCellId(s.cell);
        if (s.sunk || !p) continue;
        ctx.fillStyle = colours[s.side];
        ctx.beginPath();
        ctx.arc(p.x * px + px / 2, p.y * px + px / 2, Math.max(1.5, px * 0.45), 0, Math.PI * 2);
        ctx.fill();
      }
      // The camera's view: the screen's corners taken back onto the map (a diamond in the
      // isometric view), clipped to the map.
      const { width: vw, height: vh } = camera.viewportSize;
      const k = px / CELL;
      const corners = [
        [0, 0],
        [vw, 0],
        [vw, vh],
        [0, vh],
      ].map(([sx = 0, sy = 0]) => {
        const v = camera.screenToWorld(sx, sy);
        const w = toWorld(projection, v.x, v.y);
        return [w.x * k, w.y * k] as const;
      });
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, width, height);
      ctx.clip();
      ctx.beginPath();
      corners.forEach(([x, y], i) => {
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    };
    draw();
    // Once a frame at most: a drag moves the camera on every pointer event.
    let frame: number | null = null;
    const unsubscribe = camera.subscribe(() => {
      frame ??= requestAnimationFrame(() => {
        frame = null;
        draw();
      });
    });
    return () => {
      unsubscribe();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [camera, projection, state, colours, terrain, px, width, height]);

  // Click jumps there (animated), dragging follows the pointer.
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let down = false;
    const world = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: ((e.clientX - r.left) / px) * CELL, y: ((e.clientY - r.top) / px) * CELL };
    };
    const onDown = (e: PointerEvent) => {
      e.stopPropagation();
      down = true;
      canvas.setPointerCapture(e.pointerId);
      const w = world(e);
      const v = toView(projection, w.x, w.y);
      camera.centerOn(v.x, v.y, true);
    };
    const onMove = (e: PointerEvent) => {
      if (!down) return;
      const w = world(e);
      const v = toView(projection, w.x, w.y);
      camera.centerOn(v.x, v.y);
    };
    const onUp = () => {
      down = false;
    };
    const stop = (e: Event) => {
      e.stopPropagation();
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('wheel', stop);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('wheel', stop);
    };
  }, [camera, projection, px]);

  return (
    <canvas
      ref={ref}
      className={css.minimap}
      style={{ width, height }}
      role="img"
      aria-label="Minimap"
      data-testid="minimap"
    />
  );
}

/** The board's painted terrain from above, scaled down, with the Royal Ports in their players' colours. */
function drawTerrain(
  map: MapDef,
  px: number,
  colours: Readonly<Record<Side, string>>,
  mapStyle: MapStyle,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = map.width * px;
  canvas.height = map.height * px;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    mapStyle === 'chart' ? chartMinimapOf(map) : flatTerrainOf(map),
    0,
    0,
    canvas.width,
    canvas.height,
  );
  const idx = indexMap(map);
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const royal = idx.royalOf({ x, y });
      if (!royal) continue;
      ctx.fillStyle = colours[royal];
      ctx.fillRect(x * px, y * px, px, px);
    }
  }
  return canvas;
}
